// src\components\FiverrCard.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TbBrandFiverr } from 'react-icons/tb';
import {
  FiExternalLink,
  FiStar,
  FiUsers,
  FiClock,
  FiCheckCircle,
} from 'react-icons/fi';

// =============================================
// FIVERR URL - Centralized
// =============================================
const FIVERR_URL = 'https://www.fiverr.com/hejbul521488';

// =============================================
// GLASSY CARD - Internal component
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        borderRadius: '16px',
        boxShadow: 'none',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// FIVERR CARD - Dynamic Architecture
// =============================================
export const FiverrCard = ({
  className = '',
  variant = 'standard',
  fiverrUrl = FIVERR_URL,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // =============================================
  // VARIANT CONFIGURATIONS
  // =============================================
  const variants = {
    // =========================================
    // STANDARD - Full width, glassmorphism
    // =========================================
    standard: {
      container: 'p-4 sm:p-6 md:p-8 max-w-4xl mx-auto',
      background: 'rgba(var(--bg-rgb), 0.3)',
      border: '1px solid rgba(var(--text-rgb), 0.06)',
      backdrop: 'blur-xl',
      borderRadius: 'rounded-2xl sm:rounded-3xl',

      // Layout
      layout: 'flex-col sm:flex-row',
      alignment: 'items-center sm:items-center',
      gap: 'gap-4 sm:gap-4 md:gap-6',

      // Icon
      iconContainer: 'w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20',
      iconInner: 'w-8 h-8 sm:w-9 sm:h-9 md:w-11 md:h-11',
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      iconWrapper: 'justify-center sm:flex-shrink-0',
      iconFlex: 'flex-col sm:flex-row',

      // Content
      titleSize: 'text-sm sm:text-base md:text-lg',
      descSize: 'text-xs sm:text-sm',
      descText: 'Professional design services tailored to your needs',
      contentAlign: 'text-center sm:text-left',

      // Rating
      ratingSize: 'w-3 h-3 sm:w-3.5 sm:h-3.5',
      ratingTextSize: 'text-xs sm:text-sm',
      reviewTextSize: 'text-[10px] sm:text-xs',
      ratingPosition: 'below',
      ratingAlign: 'justify-center sm:justify-start',

      // Stats
      showStats: true,
      statsAlign: 'justify-center sm:justify-start',

      // Button
      buttonSize: 'px-5 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm',
      buttonText: 'Hire Me',
      buttonAlign: 'self-center sm:self-center',
      buttonWidth: 'w-full sm:w-auto',
      buttonMargin: 'mt-3 sm:mt-0 sm:ml-4',

      // Decorative
      showOrbs: true,
      orbSize: 'w-24 h-24 sm:w-32 sm:h-32',
      orbBlur: 'blur-2xl',
      hoverScale: 'scale-[1.01]',

      // Content wrapper
      contentWrapper: 'flex-1 min-w-0',
      leftSection: 'items-center sm:items-start',
    },

    // =========================================
    // SIDEBAR - Compact, solid background
    // =========================================
    sidebar: {
      container: 'p-4',
      background:
        'linear-gradient(135deg, var(--bg) 0%, var(--border-light) 100%)',
      border: '1px solid var(--border)',
      backdrop: 'none',
      borderRadius: 'rounded-xl',

      // Layout
      layout: 'flex-row',
      alignment: 'items-start',
      gap: 'gap-3',

      // Icon
      iconContainer: 'w-12 h-12',
      iconInner: 'w-7 h-7',
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      iconWrapper: 'flex-shrink-0',
      iconFlex: 'flex-row',

      // Content
      titleSize: 'text-sm',
      descSize: 'text-xs',
      descText: 'Professional design services',
      contentAlign: 'text-left',

      // Rating
      ratingSize: 'w-2.5 h-2.5',
      ratingTextSize: 'text-[10px]',
      reviewTextSize: 'text-[9px]',
      ratingPosition: 'with-button',
      ratingAlign: 'justify-start',

      // Stats
      showStats: false,
      statsAlign: 'justify-start',

      // Button
      buttonSize: 'px-3 py-1.5 text-[10px]',
      buttonText: 'Hire Me',
      buttonAlign: 'self-start',
      buttonWidth: 'w-auto',
      buttonMargin: 'mt-0',

      // Decorative
      showOrbs: true,
      orbSize: 'w-32 h-32',
      orbBlur: 'blur-2xl',
      hoverScale: 'scale-[1.02]',

      // Content wrapper
      contentWrapper: 'flex-1 min-w-0',
      leftSection: 'items-start',
    },

    // =========================================
    // MINI - Extra compact
    // =========================================
    mini: {
      container: 'p-3',
      background:
        'linear-gradient(135deg, var(--bg) 0%, var(--border-light) 100%)',
      border: '1px solid var(--border)',
      backdrop: 'none',
      borderRadius: 'rounded-lg',

      // Layout
      layout: 'flex-row',
      alignment: 'items-center',
      gap: 'gap-2',

      // Icon
      iconContainer: 'w-10 h-10',
      iconInner: 'w-6 h-6',
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      iconWrapper: 'flex-shrink-0',
      iconFlex: 'flex-row',

      // Content
      titleSize: 'text-xs',
      descSize: 'text-[10px]',
      descText: 'Design services',
      contentAlign: 'text-left',

      // Rating
      ratingSize: 'w-2 h-2',
      ratingTextSize: 'text-[10px]',
      reviewTextSize: 'text-[8px]',
      ratingPosition: 'below',
      ratingAlign: 'justify-start',

      // Stats
      showStats: false,
      statsAlign: 'justify-start',

      // Button
      buttonSize: 'px-2.5 py-1 text-[9px]',
      buttonText: 'Hire',
      buttonAlign: 'self-center',
      buttonWidth: 'w-auto',
      buttonMargin: 'mt-0 ml-2',

      // Decorative
      showOrbs: false,
      orbSize: 'w-16 h-16',
      orbBlur: 'blur-xl',
      hoverScale: 'scale-[1.03]',

      // Content wrapper
      contentWrapper: 'flex-1 min-w-0',
      leftSection: 'items-center',
    },
  };

  const config = variants[variant] || variants.standard;

  // =============================================
  // RENDER RATING STARS
  // =============================================
  const renderRatingStars = sizeClass => {
    const starSize = sizeClass || config.ratingSize;
    return [...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`${starSize} text-yellow-400 fill-current flex-shrink-0`}
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  // =============================================
  // RENDER RATING WITH REVIEWS
  // =============================================
  const renderRatingWithReviews = () => {
    return (
      <div
        className={`flex items-center gap-1 flex-wrap ${config.ratingAlign}`}
      >
        <div className="flex items-center gap-0.5">{renderRatingStars()}</div>
        <span
          className={`${config.ratingTextSize} font-medium`}
          style={{ color: 'var(--text)' }}
        >
          5.0
        </span>
        <span
          className={config.reviewTextSize}
          style={{ color: 'var(--muted)' }}
        >
          (2 reviews)
        </span>
      </div>
    );
  };

  // =============================================
  // RENDER STATS
  // =============================================
  const renderStats = () => {
    if (!config.showStats) return null;

    return (
      <div
        className={`flex items-center gap-1 sm:gap-2 mt-1 ${config.statsAlign}`}
      >
        <span
          className="w-px h-3 sm:h-4 flex-shrink-0"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs"
          style={{ color: 'var(--muted)' }}
        >
          <FiUsers className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
          <span>New Seller</span>
        </div>
        <span
          className="w-px h-3 sm:h-4 flex-shrink-0"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs"
          style={{ color: 'var(--muted)' }}
        >
          <FiClock className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
          <span>Fast delivery</span>
        </div>
      </div>
    );
  };

  // =============================================
  // RENDER DECORATIVE ORBS
  // =============================================
  const renderOrbs = () => {
    if (!config.showOrbs) return null;

    return (
      <>
        <div
          className={`absolute -top-10 -right-10 ${config.orbSize} rounded-full bg-green-500/5 ${config.orbBlur} group-hover:bg-green-500/10 transition-all duration-700`}
        />
        <div
          className={`absolute -bottom-10 -left-10 ${config.orbSize} rounded-full bg-blue-500/5 ${config.orbBlur} group-hover:bg-blue-500/10 transition-all duration-700`}
        />
      </>
    );
  };

  // =============================================
  // RENDER BUTTON WITH RATING (for sidebar)
  // =============================================
  const renderButtonWithRating = () => {
    return (
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-0.5">
            {renderRatingStars('w-2.5 h-2.5')}
          </div>
          <span
            className="text-[10px] font-medium"
            style={{ color: 'var(--text)' }}
          >
            5.0
          </span>
          <span className="text-[9px]" style={{ color: 'var(--muted)' }}>
            (2)
          </span>
        </div>

        <motion.a
          href={fiverrUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all duration-300 group/cta whitespace-nowrap ${config.buttonWidth}`}
          style={{
            backgroundColor: 'var(--text)',
            color: 'var(--bg)',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <span>Hire Me</span>
          <FiExternalLink className="w-3 h-3 transition-transform group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5 flex-shrink-0" />
        </motion.a>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`relative group ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`${config.borderRadius} ${config.container} transition-all duration-500 group-hover:${config.hoverScale}`}
        style={{
          backgroundColor: config.background,
          border: config.border,
          backdropFilter: config.backdrop,
          boxShadow: 'none',
        }}
      >
        {renderOrbs()}

        <div
          className={`relative z-10 flex ${config.layout} ${config.alignment} ${config.gap} justify-between w-full`}
        >
          {/* Left Section - Icon + Content */}
          <div
            className={`flex ${config.iconFlex} ${config.leftSection} gap-3 flex-1 min-w-0 w-full`}
          >
            {/* Icon */}
            <div className={config.iconWrapper}>
              <div className="relative">
                <div
                  className={`${config.iconContainer} rounded-xl flex items-center justify-center ${config.iconBg} shadow-lg shadow-green-500/10`}
                >
                  <TbBrandFiverr className={`${config.iconInner} text-white`} />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={config.contentWrapper}>
              {/* Title & Badges */}
              <div
                className={`flex items-center flex-wrap gap-1.5 mb-0.5 ${config.contentAlign} ${variant === 'standard' ? 'justify-center sm:justify-start' : 'justify-start'}`}
              >
                <h4
                  className={`${config.titleSize} font-semibold`}
                  style={{ color: 'var(--text)' }}
                >
                  Available on Fiverr
                </h4>
                <span className="px-1.5 py-0.5 rounded-full text-[8px] font-medium bg-blue-500/20 text-blue-600 dark:text-blue-400 whitespace-nowrap">
                  ✦ New Seller
                </span>
              </div>

              {/* Description */}
              <p
                className={`${config.descSize} ${config.contentAlign}`}
                style={{ color: 'var(--muted)' }}
              >
                {config.descText}
              </p>

              {/* Rating */}
              {config.ratingPosition === 'below' && (
                <div className="mt-1">{renderRatingWithReviews()}</div>
              )}

              {/* Stats */}
              {renderStats()}
            </div>
          </div>

          {/* Right Section - Button */}
          <div
            className={`${config.buttonAlign} ${config.buttonMargin} ${config.buttonWidth}`}
          >
            {config.ratingPosition === 'with-button' ? (
              renderButtonWithRating()
            ) : (
              <motion.a
                href={fiverrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center justify-center gap-1.5 ${config.buttonSize} rounded-lg font-medium transition-all duration-300 group/cta ${config.buttonWidth}`}
                style={{
                  backgroundColor: 'var(--text)',
                  color: 'var(--bg)',
                }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <span>{config.buttonText}</span>
                <FiExternalLink
                  className={`${variant === 'mini' ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5 sm:w-4 sm:h-4'} transition-transform group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5 flex-shrink-0`}
                />
              </motion.a>
            )}
          </div>
        </div>

        {/* Subtle border glow on hover - Only for standard */}
        {variant === 'standard' && (
          <div
            className="absolute inset-0 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            style={{
              boxShadow: 'inset 0 0 60px rgba(0, 230, 118, 0.015)',
            }}
          />
        )}
      </div>
    </motion.div>
  );
};

export default FiverrCard;
